/**
 * Unified control library (MERGE-PLAN §4.1).
 *
 * Ported from x365 frameworks/mappings.js and extended with SOC 2 Trust Services
 * Criteria and HIPAA Security Rule (45 CFR §164) citations. CIS / NIST / ISO /
 * Secure Score citations are carried over verbatim.
 *
 * "test once, satisfy many": each control tested by the app produces evidence for
 * every framework it maps to. Citations are drawn from published cross-mappings and
 * are a starting point — an auditor validates final applicability for the tenant.
 */
import type { ControlDef, ControlId } from './types';

export const CONTROLS: readonly ControlDef[] = [
  /* ── Intune ─────────────────────────────────────────────── */
  {
    id: 'intune_user_no_compliance_policy',
    title: 'Users with no compliance policy assigned',
    objective: 'Every user has a device compliance policy so unmanaged devices are flagged and can be blocked.',
    module: 'intune',
    severity: 'high',
    remediation:
      'Assign a device compliance policy to all users (target "All users" or every device group) so unmanaged devices are flagged.',
    frameworks: {
      cis: ['CIS-Intune-1.1', 'CIS-M365-2.1.1'],
      nist: ['CM-6', 'SI-3', 'AC-2'],
      iso: ['A.8.7', 'A.8.19'],
      soc2: ['CC6.1', 'CC6.8'],
      hipaa: ['164.308(a)(1)(ii)(B)', '164.312(a)(1)'],
      secScore: ['Require devices to be marked as compliant'],
    },
  },
  {
    id: 'intune_user_no_enrolled_device',
    title: 'Licensed users with no enrolled device',
    objective: 'Licensed users enrol a managed device, or licensing is right-sized to actual device usage.',
    module: 'intune',
    severity: 'medium',
    remediation:
      'Confirm licensed users enrol a device, or right-size licensing for users who legitimately have none.',
    frameworks: {
      cis: ['CIS-Intune-1.1'],
      nist: ['CM-8', 'AC-2'],
      iso: ['A.8.1', 'A.5.9'],
      soc2: ['CC6.1'],
      hipaa: ['164.308(a)(1)(ii)(A)', '164.310(d)(1)'],
      secScore: [],
    },
  },
  {
    id: 'intune_device_stale',
    title: 'Devices not synced within threshold',
    objective: 'Managed devices check in regularly; stale devices are investigated and retired.',
    module: 'intune',
    severity: 'medium',
    remediation:
      'Investigate and retire devices that have not synced in 30+ days; they may be lost, wiped, or decommissioned.',
    frameworks: {
      cis: ['CIS-Intune-5.1'],
      nist: ['SI-2', 'CM-6', 'SI-3'],
      iso: ['A.8.8', 'A.8.19'],
      soc2: ['CC6.1', 'CC7.1'],
      hipaa: ['164.310(d)(1)', '164.308(a)(1)(ii)(A)'],
      secScore: ['Ensure mobile device management policies are set'],
    },
  },
  {
    id: 'intune_device_non_compliant',
    title: 'Devices marked non-compliant',
    objective: 'Non-compliant devices are remediated or blocked from accessing corporate resources.',
    module: 'intune',
    severity: 'high',
    remediation:
      'Triage non-compliant devices, remediate the failing settings, or block them via a Conditional Access "require compliant device" grant.',
    frameworks: {
      cis: ['CIS-Intune-1.1', 'CIS-M365-2.1.1'],
      nist: ['CM-6', 'SI-3', 'SI-7'],
      iso: ['A.8.7', 'A.8.9'],
      soc2: ['CC6.1', 'CC7.1'],
      hipaa: ['164.312(a)(1)', '164.308(a)(1)(ii)(B)'],
      secScore: ['Require devices to be marked as compliant'],
    },
  },
  {
    id: 'intune_policy_conflict_high',
    title: 'Conflicting configuration profiles (same setting, different value)',
    objective: 'Configuration is authoritative and free of conflicting values for the same setting.',
    module: 'intune',
    severity: 'high',
    remediation:
      'Consolidate the conflicting configuration profiles into a single authoritative policy with the intended value; remove the loser.',
    frameworks: {
      cis: ['CIS-Intune-3.1'],
      nist: ['CM-6', 'CM-7', 'CM-9'],
      iso: ['A.8.9'],
      soc2: ['CC8.1'],
      hipaa: ['164.308(a)(1)(ii)(D)'],
      secScore: [],
    },
  },
  {
    id: 'intune_policy_conflict_medium',
    title: 'Overlapping configuration profiles (redundant settings)',
    objective: 'Configuration profiles are non-redundant to reduce management overhead and drift.',
    module: 'intune',
    severity: 'medium',
    remediation: 'Remove the redundant/overlapping profile to cut management overhead and drift risk.',
    frameworks: {
      cis: ['CIS-Intune-3.1'],
      nist: ['CM-6', 'CM-7'],
      iso: ['A.8.9'],
      soc2: ['CC8.1'],
      hipaa: ['164.308(a)(1)(ii)(D)'],
      secScore: [],
    },
  },
  {
    id: 'intune_no_encryption_policy',
    title: 'No device encryption policy found',
    objective: 'Managed devices enforce disk encryption (BitLocker / FileVault) to protect data at rest.',
    module: 'intune',
    severity: 'critical',
    remediation:
      'Create a policy requiring BitLocker (Windows) / FileVault (macOS) encryption and assign it to all managed devices.',
    frameworks: {
      cis: ['CIS-Intune-2.1', 'CIS-M365-2.1.3'],
      nist: ['SC-28', 'SC-28(1)'],
      iso: ['A.8.24'],
      soc2: ['CC6.7'],
      hipaa: ['164.312(a)(2)(iv)', '164.312(e)(2)(ii)'],
      secScore: ['Ensure that mobile device encryption is enabled'],
    },
  },
  {
    id: 'intune_no_password_policy',
    title: 'No device password / PIN policy found',
    objective: 'Managed devices require a password/PIN with minimum length and a lock timeout.',
    module: 'intune',
    severity: 'high',
    remediation:
      'Create a compliance policy that requires a device password/PIN with a minimum length and lock timeout.',
    frameworks: {
      cis: ['CIS-Intune-1.2'],
      nist: ['IA-5', 'IA-5(1)'],
      iso: ['A.8.5'],
      soc2: ['CC6.1'],
      hipaa: ['164.308(a)(5)(ii)(D)', '164.312(a)(2)(i)'],
      secScore: ['Ensure device password policy is enabled'],
    },
  },
  {
    id: 'intune_no_antivirus_policy',
    title: 'No antivirus / Defender for Endpoint policy found',
    objective: 'Managed devices enforce anti-malware (Microsoft Defender) protection.',
    module: 'intune',
    severity: 'critical',
    remediation: 'Deploy an Endpoint Security > Antivirus policy enforcing Microsoft Defender on all managed devices.',
    frameworks: {
      cis: ['CIS-Intune-4.1'],
      nist: ['SI-3', 'SI-3(1)'],
      iso: ['A.8.7'],
      soc2: ['CC6.8'],
      hipaa: ['164.308(a)(5)(ii)(B)'],
      secScore: ['Turn on Microsoft Defender for all supported devices'],
    },
  },
  {
    id: 'intune_unassigned_policy',
    title: 'Policies with no group assignments (orphaned)',
    objective: 'Every policy is assigned to a group or removed — no orphaned, ineffective policies.',
    module: 'intune',
    severity: 'low',
    remediation: 'Assign orphaned policies to the correct groups, or delete them if they are no longer needed.',
    frameworks: {
      cis: [],
      nist: ['CM-6'],
      iso: ['A.8.9'],
      soc2: ['CC8.1'],
      hipaa: ['164.308(a)(1)(ii)(D)'],
      secScore: [],
    },
  },
  {
    id: 'intune_group_no_coverage',
    title: 'Groups with partial policy coverage by platform',
    objective: 'Every platform in each group has a matching compliance/config policy.',
    module: 'intune',
    severity: 'medium',
    remediation: 'Extend policy coverage so every platform in each group has a matching compliance/config policy.',
    frameworks: {
      cis: ['CIS-Intune-1.1'],
      nist: ['CM-6', 'AC-2'],
      iso: ['A.8.9'],
      soc2: ['CC6.1'],
      hipaa: ['164.312(a)(1)'],
      secScore: [],
    },
  },

  /* ── Identity ───────────────────────────────────────────── */
  {
    id: 'identity_user_no_mfa',
    title: 'Users with no MFA method registered',
    objective: 'Every user has a strong MFA method registered and enforced.',
    module: 'identity',
    severity: 'critical',
    remediation:
      'Require MFA registration for every user via a Conditional Access policy or the Authentication Methods policy.',
    frameworks: {
      cis: ['CIS-M365-1.1.1', 'CIS-M365-1.1.2'],
      nist: ['IA-2', 'IA-2(1)', 'IA-2(2)'],
      iso: ['A.8.5'],
      soc2: ['CC6.1'],
      hipaa: ['164.312(d)', '164.308(a)(5)(ii)(C)'],
      secScore: ['Require MFA for all users', 'Require MFA for administrative roles'],
    },
  },
  {
    id: 'identity_user_no_ca_policy',
    title: 'Users not in scope of any Conditional Access policy',
    objective: 'Every user is covered by Conditional Access enforcing MFA and device/location controls.',
    module: 'identity',
    severity: 'high',
    remediation: 'Create Conditional Access policies scoped to All users to enforce MFA and device/location controls.',
    frameworks: {
      cis: ['CIS-M365-1.1.3'],
      nist: ['AC-2', 'AC-17', 'IA-2'],
      iso: ['A.8.3', 'A.9.4.2'],
      soc2: ['CC6.1', 'CC6.6'],
      hipaa: ['164.312(a)(1)', '164.308(a)(4)'],
      secScore: ['Enable Conditional Access policies'],
    },
  },
  {
    id: 'identity_admin_no_pim',
    title: 'Privileged roles assigned permanently (no PIM)',
    objective: 'Privileged roles use just-in-time (PIM) activation with approval and time limits.',
    module: 'identity',
    severity: 'high',
    remediation:
      'Move permanent privileged assignments to PIM eligible (just-in-time) activation with approval and time limits.',
    frameworks: {
      cis: ['CIS-M365-1.5'],
      nist: ['AC-5', 'AC-6', 'AC-6(5)'],
      iso: ['A.5.18', 'A.8.2'],
      soc2: ['CC6.3'],
      hipaa: ['164.308(a)(3)(ii)(A)', '164.308(a)(4)'],
      secScore: ['Use limited privileges'],
    },
  },
  {
    id: 'identity_global_admin_sprawl',
    title: 'Excessive Global Administrator accounts',
    objective: 'Global Administrators are limited to 2–4 accounts; others use least-privilege roles.',
    module: 'identity',
    severity: 'high',
    remediation: 'Reduce Global Administrators to 2-4; downgrade the rest to least-privilege roles managed through PIM.',
    frameworks: {
      cis: ['CIS-M365-1.3'],
      nist: ['AC-2', 'AC-5', 'AC-6'],
      iso: ['A.5.18'],
      soc2: ['CC6.3'],
      hipaa: ['164.308(a)(3)', '164.308(a)(4)'],
      secScore: ['Designate more than one global admin', 'Limit number of global admins to less than 5'],
    },
  },
  {
    id: 'identity_stale_guest',
    title: 'Guest users inactive for more than 90 days',
    objective: 'External/guest identities are reviewed and removed when inactive.',
    module: 'identity',
    severity: 'medium',
    remediation: 'Review and remove guest accounts with no sign-in in 90+ days; enable access reviews for external identities.',
    frameworks: {
      cis: ['CIS-M365-5.1'],
      nist: ['AC-2', 'AC-2(3)'],
      iso: ['A.5.18', 'A.9.2.6'],
      soc2: ['CC6.2', 'CC6.3'],
      hipaa: ['164.308(a)(3)(ii)(C)', '164.308(a)(4)'],
      secScore: ['Review and remove external users who have not signed in recently'],
    },
  },
  {
    id: 'identity_legacy_auth_allowed',
    title: 'Legacy authentication protocols not blocked',
    objective: 'Legacy authentication (which bypasses MFA) is blocked tenant-wide.',
    module: 'identity',
    severity: 'critical',
    remediation: 'Add a Conditional Access policy that blocks legacy authentication clients (Exchange ActiveSync / other).',
    frameworks: {
      cis: ['CIS-M365-1.1.4'],
      nist: ['IA-2', 'SC-8'],
      iso: ['A.8.5'],
      soc2: ['CC6.6', 'CC6.7'],
      hipaa: ['164.312(e)(1)', '164.312(d)'],
      secScore: ['Block legacy authentication'],
    },
  },
  {
    id: 'identity_admin_no_dedicated_account',
    title: 'Admin roles assigned to regular user accounts (no dedicated admin account)',
    objective: 'Administrators use separate cloud-only admin accounts distinct from daily-use accounts.',
    module: 'identity',
    severity: 'medium',
    remediation: 'Give administrators separate cloud-only admin accounts distinct from their day-to-day user accounts.',
    frameworks: {
      cis: ['CIS-M365-1.4'],
      nist: ['AC-5', 'AC-6'],
      iso: ['A.5.18'],
      soc2: ['CC6.3'],
      hipaa: ['164.308(a)(4)(ii)(B)'],
      secScore: [],
    },
  },
  {
    id: 'identity_no_security_baseline',
    title: 'No baseline protection — Security Defaults off and no enabled CA policy',
    objective: 'The tenant has a baseline (Security Defaults or Conditional Access) enforcing MFA and blocking legacy auth.',
    module: 'identity',
    severity: 'critical',
    remediation: 'Turn on Security Defaults, or (better) build Conditional Access policies that enforce MFA and block legacy auth.',
    frameworks: {
      cis: ['CIS-M365-1.1.1', 'CIS-M365-1.1.3'],
      nist: ['AC-2', 'IA-2', 'CM-6'],
      iso: ['A.5.15', 'A.8.5'],
      soc2: ['CC6.1'],
      hipaa: ['164.308(a)(1)', '164.312(d)'],
      secScore: ['Enable Security Defaults', 'Enable Conditional Access policies'],
    },
  },
  {
    id: 'identity_stale_member',
    title: 'Enabled member accounts inactive for more than 90 days',
    objective: 'Inactive enabled accounts are disabled or removed.',
    module: 'identity',
    severity: 'medium',
    remediation: 'Disable or remove enabled member accounts with no sign-in in 90+ days; automate with access reviews.',
    frameworks: {
      cis: ['CIS-M365-1.1.9'],
      nist: ['AC-2', 'AC-2(3)'],
      iso: ['A.5.18', 'A.8.2'],
      soc2: ['CC6.2'],
      hipaa: ['164.308(a)(3)(ii)(C)'],
      secScore: ['Remove or block accounts that have not signed in recently'],
    },
  },

  /* ── Applications & Consent ─────────────────────────────── */
  {
    id: 'app_credential_expiring',
    title: 'App registration secrets/certificates expiring within 30 days',
    objective: 'App credentials are rotated before expiry to avoid outage and stale secrets.',
    module: 'applications',
    severity: 'high',
    remediation: 'Rotate the app secret/certificate before expiry to avoid an outage; prefer certificates and short lifetimes.',
    frameworks: {
      cis: ['CIS-M365-1.2.2'],
      nist: ['IA-5', 'IA-5(1)', 'SC-12'],
      iso: ['A.5.16', 'A.8.24'],
      soc2: ['CC6.1', 'A1.2'],
      hipaa: ['164.312(d)', '164.308(a)(1)(ii)(A)'],
      secScore: [],
    },
  },
  {
    id: 'app_credential_expired',
    title: 'App registrations with expired credentials still present',
    objective: 'Expired secrets/certificates are removed from app registrations.',
    module: 'applications',
    severity: 'medium',
    remediation: 'Delete expired secrets/certificates left on app registrations; they are dead weight and audit noise.',
    frameworks: {
      cis: ['CIS-M365-1.2.2'],
      nist: ['IA-5', 'CM-8'],
      iso: ['A.5.16', 'A.8.9'],
      soc2: ['CC6.1'],
      hipaa: ['164.312(d)'],
      secScore: [],
    },
  },
  {
    id: 'app_risky_consent',
    title: 'Tenant-wide consent granting high-privilege Graph permissions',
    objective: 'Tenant-wide app consent is reviewed and limited to least-privilege, verified publishers.',
    module: 'applications',
    severity: 'critical',
    remediation:
      'Review the tenant-wide consent, remove unneeded high-privilege Graph scopes, and restrict user consent to verified publishers.',
    frameworks: {
      cis: ['CIS-M365-1.3.3', 'CIS-M365-2.4'],
      nist: ['AC-6', 'AC-6(1)', 'CM-7'],
      iso: ['A.5.18', 'A.8.2', 'A.8.18'],
      soc2: ['CC6.3'],
      hipaa: ['164.308(a)(4)', '164.312(a)(1)'],
      secScore: ['Review permissions granted to applications', 'Do not allow users to grant consent to unmanaged applications'],
    },
  },
  {
    id: 'app_multitenant',
    title: 'Multi-tenant / personal-account app registrations owned by the tenant',
    objective: 'App sign-in audiences (multi-tenant/personal) are intentional, not accidental exposure.',
    module: 'applications',
    severity: 'low',
    remediation: 'Confirm multi-tenant / personal-account sign-in audiences are intentional; switch to single-tenant where not required.',
    frameworks: {
      cis: ['CIS-M365-1.3'],
      nist: ['CM-7', 'AC-3'],
      iso: ['A.8.9'],
      soc2: ['CC6.3'],
      hipaa: ['164.308(a)(4)'],
      secScore: [],
    },
  },
];

/** Index for O(1) lookup by control id. */
export const CONTROL_BY_ID: Readonly<Record<ControlId, ControlDef>> = Object.fromEntries(
  CONTROLS.map((c) => [c.id, c]),
);

export function getControl(id: ControlId): ControlDef | undefined {
  return CONTROL_BY_ID[id];
}
